import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Trophy, Loader2, Users } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const careerHref = (leaderboardToken, teamName, playerName, eventId) => {
  if (!leaderboardToken) return null;
  const params = new URLSearchParams({ team: teamName, name: playerName });
  if (eventId) params.append('event_id', eventId);
  return `/stats/leaderboard/${leaderboardToken}/career?${params.toString()}`;
};

export function PublicLeaderboardView({ shareToken, eventId, leaderboardShareToken, eventTeams = [] }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // team_name (lowercased) -> team_id, so we can link team names to team detail pages.
  const teamIdByName = React.useMemo(() => {
    const m = {};
    (eventTeams || []).forEach(t => {
      const nm = (t.name || '').trim().toLowerCase();
      if (nm) m[nm] = t.id;
    });
    return m;
  }, [eventTeams]);

  const teamLink = (teamName) => {
    if (!shareToken || !teamName) return null;
    const tid = teamIdByName[teamName.trim().toLowerCase()];
    return tid ? `/share/${shareToken}/team/${tid}` : null;
  };

  useEffect(() => {
    if (!shareToken) return;
    fetch(`${API_URL}/api/share/${shareToken}/leaderboard`)
      .then(r => r.ok ? r.json() : null)
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [shareToken]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-[#00247D]" />
      </div>
    );
  }

  if (!data || data.games_count === 0) {
    return (
      <Card className="border-dashed border-slate-300 bg-slate-50" data-testid="public-leaderboard-empty">
        <CardContent className="py-12 text-center">
          <Trophy className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-600 font-semibold mb-1">Leaderboard not available yet</p>
          <p className="text-slate-500 text-sm">Leaderboard populates once games with stats are recorded.</p>
        </CardContent>
      </Card>
    );
  }

  const statLeaders = data.stat_leaders || {};
  const perRankings = data.per_rankings || [];

  return (
    <div className="space-y-6" data-testid="public-leaderboard-view">
      {/* Header chip */}
      <div className="flex items-center gap-2 text-sm text-slate-600">
        <Trophy className="w-4 h-4 text-amber-500" />
        <span>{data.games_count} games tracked · {perRankings.length} players</span>
      </div>

      {/* Stat Leaders */}
      <div>
        <h3 className="text-lg font-bold text-[#00247D] mb-3 uppercase tracking-wide" style={{ fontFamily: 'Barlow Condensed' }}>
          Stat Leaders
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          {Object.entries(statLeaders).map(([key, section]) => (
            <Card key={key} className="overflow-hidden border-slate-200">
              <div className="bg-slate-900 text-amber-400 px-3 py-2">
                <h4 className="text-xs font-bold uppercase tracking-widest">{section.label}</h4>
              </div>
              <CardContent className="p-3">
                <div className="space-y-2">
                  {section.leaders.map((p, i) => {
                    const href = careerHref(leaderboardShareToken, p.team_name, p.name, eventId);
                    const Inner = (
                      <span className="flex items-center gap-2">
                        <span className={`w-4 text-center font-bold ${i === 0 ? 'text-amber-500' : 'text-slate-400'}`}>{i + 1}</span>
                        {p.headshot_path ? (
                          <img src={`${API_URL}/api/files/${p.headshot_path}`} alt="" className="w-5 h-5 rounded-full object-cover shrink-0" onError={e => { e.target.style.display = 'none'; }} />
                        ) : (
                          <span className="font-mono text-[10px] text-slate-400 w-5 text-center">#{p.jersey}</span>
                        )}
                        <span className="flex-1 truncate text-slate-800">{p.name}</span>
                        <span className="font-bold text-[#00247D]">{p.per_game}</span>
                      </span>
                    );
                    return (
                      <div key={i} className="text-xs">
                        {href ? (
                          <Link to={href} className="block hover:bg-slate-50 rounded px-1 -mx-1 py-0.5"
                            data-testid={`pubsched-leader-${key}-${i}`}>
                            {Inner}
                          </Link>
                        ) : Inner}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Full rankings */}
      <div>
        <h3 className="text-lg font-bold text-[#00247D] mb-3 uppercase tracking-wide flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed' }}>
          <Users className="w-4 h-4" /> All Players
        </h3>
        <Card className="overflow-hidden border-slate-200">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[760px]">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-3 py-2 text-center text-[11px] font-bold text-slate-500 uppercase w-10">#</th>
                  <th className="px-3 py-2 text-left text-[11px] font-bold text-slate-500 uppercase">Player</th>
                  <th className="px-3 py-2 text-left text-[11px] font-bold text-slate-500 uppercase">Team</th>
                  <th className="px-3 py-2 text-center text-[11px] font-bold text-slate-500 uppercase">GP</th>
                  <th className="px-3 py-2 text-center text-[11px] font-bold text-slate-500 uppercase">PPG</th>
                  <th className="px-3 py-2 text-center text-[11px] font-bold text-slate-500 uppercase">RPG</th>
                  <th className="px-3 py-2 text-center text-[11px] font-bold text-slate-500 uppercase">APG</th>
                  <th className="px-3 py-2 text-center text-[11px] font-bold text-slate-500 uppercase">SPG</th>
                  <th className="px-3 py-2 text-center text-[11px] font-bold text-slate-500 uppercase">BPG</th>
                  <th className="px-3 py-2 text-center text-[11px] font-bold text-slate-500 uppercase">FG%</th>
                </tr>
              </thead>
              <tbody>
                {perRankings.map((p, i) => {
                  const href = careerHref(leaderboardShareToken, p.team_name, p.name, eventId);
                  const tHref = teamLink(p.team_name);
                  return (
                    <tr key={`${p.name}-${p.team_name}`} className={`border-b border-slate-100 ${i < 3 ? 'bg-amber-50/40' : ''}`} data-testid={`public-leaderboard-row-${i}`}>
                      <td className="px-3 py-2 text-center text-slate-500 font-bold">{i + 1}</td>
                      <td className="px-3 py-2 font-semibold text-slate-800">
                        {href ? (
                          <Link to={href} className="inline-flex items-center gap-2 hover:text-amber-600 hover:underline underline-offset-2"
                            data-testid={`pubsched-per-player-link-${i}`}>
                            {p.headshot_path && <img src={`${API_URL}/api/files/${p.headshot_path}`} alt="" className="w-5 h-5 rounded-full object-cover shrink-0" onError={e => { e.target.style.display = 'none'; }} />}
                            <span>{p.name}</span>
                          </Link>
                        ) : (
                          <span className="inline-flex items-center gap-2">
                            {p.headshot_path && <img src={`${API_URL}/api/files/${p.headshot_path}`} alt="" className="w-5 h-5 rounded-full object-cover shrink-0" onError={e => { e.target.style.display = 'none'; }} />}
                            <span>{p.name}</span>
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-slate-500">
                        {tHref ? (
                          <Link to={tHref} className="inline-flex items-center gap-1.5 hover:text-amber-600 hover:underline underline-offset-2"
                            data-testid={`pubsched-team-link-${i}`}>
                            {p.team_logo && <img src={`${API_URL}/api/files/${p.team_logo}`} alt="" className="w-4 h-4 rounded-full object-cover" onError={e => { e.target.style.display = 'none'; }} />}
                            {p.team_name}
                          </Link>
                        ) : (
                          <span className="inline-flex items-center gap-1.5">
                            {p.team_logo && <img src={`${API_URL}/api/files/${p.team_logo}`} alt="" className="w-4 h-4 rounded-full object-cover" onError={e => { e.target.style.display = 'none'; }} />}
                            {p.team_name}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-center text-slate-500">{p.gp}</td>
                      <td className="px-3 py-2 text-center font-bold text-[#00247D]">{p.ppg}</td>
                      <td className="px-3 py-2 text-center text-slate-700">{p.rpg}</td>
                      <td className="px-3 py-2 text-center text-slate-700">{p.apg}</td>
                      <td className="px-3 py-2 text-center text-slate-700">{p.spg}</td>
                      <td className="px-3 py-2 text-center text-slate-700">{p.bpg}</td>
                      <td className="px-3 py-2 text-center text-slate-500">{p.fg_pct}%</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </div>
  );
}
